import type { ReactNode } from 'react';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { IconName } from '../icon';
import InlineTextButton from '../inline-text-button';

export interface ExpandableContentProps {
  children: (isExpanded: boolean) => ReactNode;
  shouldShowButton?: boolean;
  shouldShowDivider?: boolean;
  className?: string;
  /** Override the "show more" button label. Defaults to the `words.showMore` translation. */
  showMoreLabel?: string;
  /** Override the "show less" button label. Defaults to the `words.showLess` translation. */
  showLessLabel?: string;
}

const ExpandableContent = ({
  children,
  shouldShowButton = true,
  shouldShowDivider = true,
  className,
  showMoreLabel,
  showLessLabel,
}: ExpandableContentProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Wrapper className={className}>
      {children(isExpanded)}
      {shouldShowButton && (
        <Container>
          {shouldShowDivider && <Divider />}
          <InlineTextButton
            label={
              isExpanded
                ? (showLessLabel ?? t('words.showLess'))
                : (showMoreLabel ?? t('words.showMore'))
            }
            onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
            isRightIcon
            iconName={isExpanded ? IconName.ArrowsUp : IconName.ArrowsDown}
          />
        </Container>
      )}
    </Wrapper>
  );
};

export default memo(ExpandableContent);

const Wrapper = styled.div`
  width: 100%;
`;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    margin-top: ${theme.spacing(2)};
  `}
`;

const Divider = styled.div`
  ${({ theme }) => css`
    height: 1px;
    background-color: ${theme.palette.neutral[60]};
  `}
`;
